import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip, Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { $note } from '@nx-frontend/shared/store/note';
import { $notesTags, deleteTagFromNoteEvent, fetchNoteTagsEvent } from '@nx-frontend/shared/store/tags';
import { $workspaceAccess } from '@nx-frontend/shared/store/workspace-access';
import { UiTooltipPosition, WorkspaceRole } from '@nx-frontend/shared/types';
import { TagsSelectPopup } from './tag-select-popup';

function NoteTagItem(props: { tag: string }) {
  const { tag } = props;

  const [currentNote, workspaceAccess] = useUnit([$note, $workspaceAccess]);

  const deleteTagFromNote = useEvent(deleteTagFromNoteEvent);

  const handleDeleteTagClick = useCallback(() => {
    deleteTagFromNote({ noteId: currentNote.globalId, tag });
  }, [tag, deleteTagFromNote, currentNote]);

  const showedCharactersLimit = 15;
  const showTooltip = useMemo(() => tag.length > showedCharactersLimit, [tag]);
  const showedText = useMemo(() => {
    return tag.length > showedCharactersLimit ? tag.slice(0, showedCharactersLimit) + '...' : tag;
  }, [tag]);

  const tagChip = useMemo(
    () => (
      <div className="rounded-lg px-3 h-[24px] flex items-center gap-1.5" style={{ backgroundColor: '#eee' }}>
        <div className="text-sm flex items-center">{showedText}</div>
        {[WorkspaceRole.ADMIN, WorkspaceRole.EDITOR].includes(workspaceAccess.role) && currentNote.editnote && (
          <div className="w-4 h-4 cursor-pointer text-neutral-400" onClick={handleDeleteTagClick}>
            <Icon icon={IconsMap.CLOSE} />
          </div>
        )}
      </div>
    ),
    [showedText, handleDeleteTagClick, workspaceAccess.role, currentNote.editnote]
  );

  return !showTooltip ? (
    tagChip
  ) : (
    <Tooltip message={tag} position={UiTooltipPosition.BOTTOM}>
      {tagChip}
    </Tooltip>
  );
}

function AddTagButton() {
  const [showSelectPopup, setShowSelectPopup] = useState(false);

  const [currentNote, notesTags, workspaceAccess] = useUnit([$note, $notesTags, $workspaceAccess]);

  const t = useTranslations('webnotes');

  const userCanAddTags = useMemo(() => {
    return [WorkspaceRole.ADMIN, WorkspaceRole.EDITOR].includes(workspaceAccess.role);
  }, [workspaceAccess.role]);

  const addButtonInactive = useMemo(() => {
    const currentNoteTags = notesTags.notes[currentNote.globalId];
    return (
      !currentNote.editnote ||
      !userCanAddTags ||
      !currentNoteTags ||
      (!currentNoteTags?.tags?.length && currentNoteTags?.isFetching)
    );
  }, [currentNote, notesTags, userCanAddTags]);

  const addTagButtonTooltip: string | undefined = useMemo(() => {
    if (!userCanAddTags) {
      return t('webnotes-no_rights_to_add_tag');
    }
    if (!currentNote.editnote) {
      return t('webnotes-cant_add_tag_readonly');
    }
    return undefined;
  }, [userCanAddTags, currentNote, t]);

  const targetRef = useRef<HTMLDivElement>(null);
  const addTagButton = useMemo(() => {
    return (
      <div
        className={classNames('flex items-center gap-1.5', 'text-sm font-medium', {
          'text-neutral-400 hover:text-cyan-700 cursor-pointer': !addButtonInactive,
          'text-neutral-300': addButtonInactive,
        })}
        onClick={() => {
          if (!addButtonInactive) {
            setShowSelectPopup(true);
          }
        }}
      >
        <Icon className="w-4 h-4" icon={IconsMap.TAG} />
        {t('webnotes-add_tag')}
      </div>
    );
  }, [t, addButtonInactive]);

  return (
    <div className="flex">
      <div ref={targetRef}></div>
      {addTagButtonTooltip ? (
        <TippyTooltip message={addTagButtonTooltip} position={'bottom-start'}>
          {addTagButton}
        </TippyTooltip>
      ) : (
        addTagButton
      )}
      <TagsSelectPopup
        show={showSelectPopup}
        targetRef={targetRef}
        onClose={() => {
          setShowSelectPopup(false);
        }}
      />
    </div>
  );
}

export function NoteTags() {
  const [currentNote, notesTags] = useUnit([$note, $notesTags]);
  const fetchNoteTags = useEvent(fetchNoteTagsEvent);

  useEffect(() => {
    fetchNoteTags({ noteId: currentNote.globalId });
  }, [fetchNoteTags, currentNote]);

  const currentNoteTags = useMemo(() => {
    return notesTags.notes[currentNote.globalId]?.tags ?? [];
  }, [notesTags, currentNote]);

  return (
    <div className="w-full">
      <div className="pr-[150px] flex gap-[5px] items-center flex-wrap">
        {currentNoteTags.map((tag) => (
          <NoteTagItem tag={tag} />
        ))}
        <AddTagButton />
      </div>
    </div>
  );
}
